/**
 * ETL v2 — Expression AST.
 *
 * A compact, typed expression language used inside `if` / `elseif` /
 * `unless` / `when` / `until` / `forEach` / `expect EXPR` / `set X = …`
 * and inside `${…}` interpolation in narration / locator strings.
 *
 * The evaluator is a pure interpreter — no `eval`, no `Function`, no
 * prototype walking. Member access is allow-listed at runtime by
 * {@link expr-eval}; the AST itself only describes shape.
 *
 * Designed to be parser-stable: every node carries enough information to
 * round-trip through JSON and to produce a deterministic snapshot.
 */

import type { Locator } from './tour-types';

/** Literal scalar values permitted in an expression. */
export type ExprLiteralValue = string | number | boolean | null;

export type ExprBinaryOp =
  | '==' | '!=' | '<' | '<=' | '>' | '>='
  | '&&' | '||'
  | '+' | '-' | '*' | '/' | '%';

export type ExprUnaryOp = '!' | '-';

/**
 * Whitelist of pure functions exposed to expressions. The evaluator
 * rejects calls to anything outside this set.
 *
 * - `exists(loc)` / `visible(loc)` / `count(loc)` / `text(loc)` / `value(loc)`
 *   — DOM predicates (host-provided at runtime; pure interpreter under jsdom)
 * - `length(s)` / `lower(s)` / `upper(s)` / `contains(haystack, needle)`
 *   — string helpers
 * - `now()` — current time in ms (Date.now())
 * - `can(action, resource?)` — host-provided permission check; cached at
 *   tour start. Resolves synchronously (prefetched).
 */
export type ExprBuiltinFn =
  | 'exists'
  | 'visible'
  | 'count'
  | 'text'
  | 'value'
  | 'length'
  | 'lower'
  | 'upper'
  | 'contains'
  | 'now'
  | 'can';

export interface ExprLit { kind: 'lit'; value: ExprLiteralValue; }

/**
 * Bare identifier reference. Resolved against the variable scope by
 * walking inside-out. Top-level identifiers are gated by the evaluator's
 * allow-list (`params`, `vars`, `user`, `tenant`, `flags`, `env`,
 * `route`).
 */
export interface ExprIdent { kind: 'ident'; name: string; }

/** Member access: `a.b`. */
export interface ExprMember { kind: 'member'; obj: Expr; prop: string; }

/** Indexed access: `a[expr]`. */
export interface ExprIndex { kind: 'index'; obj: Expr; key: Expr; }

export interface ExprUnary { kind: 'unary'; op: ExprUnaryOp; arg: Expr; }

export interface ExprBinary {
  kind: 'binary';
  op: ExprBinaryOp;
  left: Expr;
  right: Expr;
}

/**
 * Call expression. `fn` is restricted to {@link ExprBuiltinFn}; user-defined
 * functions are not callable from inside an expression. (Macros are a
 * separate concept and are invoked only by the `call` step verb.)
 */
export interface ExprCall {
  kind: 'call';
  fn: ExprBuiltinFn;
  args: Expr[];
}

/**
 * Template string with interpolation: `"Hello ${user.name}!"`.
 *
 * `parts` alternates between literal string fragments (kept as `string`)
 * and embedded expressions (`Expr`). Either may be present at any
 * position; consecutive strings are merged at parse time.
 */
export interface ExprTemplate {
  kind: 'tpl';
  parts: ReadonlyArray<string | Expr>;
}

export interface ExprRegex {
  kind: 'regex';
  source: string;
  flags: string;
}

/**
 * A locator literal embedded inside an expression. Used by predicate
 * builtins like `exists(@role.alert:"Saved")`. Evaluator returns the
 * locator value untouched; the calling builtin is responsible for
 * resolving it against the DOM.
 */
export interface ExprLocator { kind: 'locator'; locator: Locator; }

export type Expr =
  | ExprLit
  | ExprIdent
  | ExprMember
  | ExprIndex
  | ExprUnary
  | ExprBinary
  | ExprCall
  | ExprTemplate
  | ExprRegex
  | ExprLocator;

/**
 * Set of identifiers permitted at the **top level** of expression
 * resolution. Anything else thrown by the evaluator is a `ScopeError`.
 *
 * Lower-cased; matched exactly. The evaluator allows arbitrary nesting
 * underneath these (e.g. `user.roles[0]`) but does not walk prototypes.
 */
export const TOP_LEVEL_IDENTS: ReadonlySet<string> = new Set([
  'params',
  'vars',
  'user',
  'tenant',
  'flags',
  'env',
  'route',
  'error', // available inside catch-block frames
  // Allow standalone references to literal "true"/"false"/"null" — the
  // parser maps them to literals before reaching this list, but the
  // executor's expression layer also accepts them as identifiers for
  // robustness against partial JSON input.
  'true',
  'false',
  'null',
]);

/**
 * Set of builtin function names. Mirrors {@link ExprBuiltinFn} but as a
 * runtime-checkable Set for the parser/evaluator. Keep in sync.
 */
export const EXPR_BUILTIN_FNS: ReadonlySet<ExprBuiltinFn> = new Set<ExprBuiltinFn>([
  'exists',
  'visible',
  'count',
  'text',
  'value',
  'length',
  'lower',
  'upper',
  'contains',
  'now',
  'can',
]);

/**
 * Built-in arity table. The parser uses this for early errors; the
 * evaluator double-checks at runtime. `null` means variadic (none today).
 */
export const EXPR_BUILTIN_ARITY: Readonly<Record<ExprBuiltinFn, number>> = Object.freeze({
  exists: 1,
  visible: 1,
  count: 1,
  text: 1,
  value: 1,
  length: 1,
  lower: 1,
  upper: 1,
  contains: 2,
  now: 0,
  can: 1, // 1 or 2; runtime accepts both — parser uses min arity
});

/**
 * Errors raised by the expression layer. Distinct from {@link ParseError}
 * (grammar errors at the host parser level) so callers can decide
 * whether to surface as a parse-time error or a runtime ScopeError.
 */
export class ExprParseError extends Error {
  constructor(
    message: string,
    public readonly sourceLine: number,
    public readonly column?: number,
  ) {
    const where = column !== undefined ? `${sourceLine}:${column}` : `${sourceLine}`;
    super(`[ETL] Expression parse error on line ${where}: ${message}`);
    this.name = 'ExprParseError';
  }
}

export class ExprEvalError extends Error {
  constructor(message: string) {
    super(`[ETL] Expression eval error: ${message}`);
    this.name = 'ExprEvalError';
  }
}
